/**
 * React hook wrapping the pure comparator-addition-window helpers
 * ({@link ./comparatorAdditionWindow}) with the time-awareness the page needs.
 *
 * The pure helpers resolve the deadline and the open/passed state for a given
 * `now`, but `useMemo` does not track the passage of time — so on an idle or
 * lightly-interacting page the add/send affordances and banner copy would stay
 * frozen in their pre-deadline state after the deadline elapsed. This hook adds
 * a single self-scheduling timer that forces exactly one re-render at the
 * deadline, so the derived state flips on its own.
 *
 * Kept as a standalone hook (not inline in `OrderInProgress.tsx`) so the
 * time-scheduling behavior can grow (e.g. countdown copy, warning thresholds,
 * server-clock skew handling) and be tested in isolation, while the page just
 * consumes the resolved `{ open, window }` state.
 */

import { useEffect, useMemo, useState } from 'react'

import {
  comparatorAdditionDeadlineMs,
  isComparatorAdditionWindowOpen,
  resolveComparatorAdditionWindow,
  type ComparatorAdditionWindowInput,
} from './comparatorAdditionWindow'

/** `setTimeout` clamps delays above this (~24.8 days) and would fire immediately. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1

export interface ComparatorAdditionWindowState {
  /**
   * Whether adding NEW comparators is currently allowed (window applies AND
   * `now` is at/before the deadline). Drives the add/send affordance gates.
   */
  open: boolean
  /**
   * Banner-facing `{ label, passed }` (human-readable deadline + whether it has
   * elapsed), or `null` when the window does not apply to this order so the
   * banner omits all window messaging.
   */
  window: { label: string; passed: boolean } | null
  /**
   * The resolved deadline in epoch ms, or `null` when the window does not apply.
   * Exposed for callers that need the raw boundary (tests, future countdowns).
   */
  deadlineMs: number | null
}

/**
 * Resolve the comparator-addition-window state for the current order and keep
 * it live across the deadline boundary.
 *
 * Behavior:
 * - `open` / `window.passed` are computed from the injected pure helpers.
 * - A single `setTimeout` fires at the deadline to re-render, so an otherwise
 *   idle page flips from open→closed (and the banner from "can add until…" to
 *   "window closed…") without any user interaction. Far-future deadlines
 *   (beyond the `setTimeout` clamp) are not scheduled; a normal interaction or
 *   status refetch recomputes long before then.
 */
export function useComparatorAdditionWindow(input: ComparatorAdditionWindowInput): ComparatorAdditionWindowState {
  const { orderStatus, selectedOption, accessionedOn, config } = input

  const deadlineMs = useMemo(
    () => comparatorAdditionDeadlineMs({ orderStatus, selectedOption, accessionedOn, config }),
    [orderStatus, selectedOption, accessionedOn, config]
  )

  // `nowTick` is bumped by the deadline timer purely to invalidate the memos
  // below; its value is otherwise irrelevant.
  const [nowTick, setNowTick] = useState(0)
  useEffect(() => {
    if (deadlineMs === null) return
    const msUntilDeadline = deadlineMs - Date.now()
    if (msUntilDeadline <= 0) return
    if (msUntilDeadline > MAX_TIMEOUT_MS) return
    const id = window.setTimeout(() => setNowTick((t) => t + 1), msUntilDeadline)
    return () => window.clearTimeout(id)
  }, [deadlineMs])

  const open = useMemo(
    () => isComparatorAdditionWindowOpen({ orderStatus, selectedOption, accessionedOn, config }),
    // `nowTick` intentionally invalidates this so the window closes when the
    // deadline timer fires on an otherwise-idle page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderStatus, selectedOption, accessionedOn, config, nowTick]
  )

  const windowState = useMemo(
    () => resolveComparatorAdditionWindow({ orderStatus, selectedOption, accessionedOn, config }),
    // `nowTick` flips `passed` when the deadline timer fires on an idle page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderStatus, selectedOption, accessionedOn, config, nowTick]
  )

  return { open, window: windowState, deadlineMs }
}
